import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReviewerLayout from '../../components/Layout/ReviewerLayout';
import DeadlineBanner from '../../components/DeadlineBanner';
import { api } from '../../services/api';
import type { ReviewerApplicationSummary, DeadlinesConfig } from '@vedjegy/shared';
import { APPLICATION_STATUS_LABELS } from '../../utils/applicationStatus';

const STATUS_LABELS = APPLICATION_STATUS_LABELS;

export default function ReviewerDashboardPage() {
  const navigate = useNavigate();
  const [apps, setApps]         = useState<ReviewerApplicationSummary[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [deadlines, setDeadlines] = useState<DeadlinesConfig | null>(null);

  useEffect(() => {
    Promise.all([
      api.reviewer.listApplications(),
      api.config.getDeadlines().catch(() => null),
    ])
      .then(([appsRes, deadlineRes]) => {
        setApps(appsRes.applications);
        setDeadlines(deadlineRes);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Betöltési hiba.'))
      .finally(() => setLoading(false));
  }, []);

  const pending   = apps.filter((a) => !a.hasFinalized);
  const finalized = apps.filter((a) => a.hasFinalized);

  return (
    <ReviewerLayout>
      <div style={{ padding: '32px 40px' }}>
        {/* Deadline countdown banner */}
        {deadlines && <DeadlineBanner type="review" deadlines={deadlines} />}

        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 6 }}>
            BÍRÁLÓI FELÜLET
          </div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--primary)' }}>Hozzárendelt pályázatok</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--muted-foreground)' }}>
            {apps.length} pályázat összesen · {pending.length} vár pontozásra
          </p>
        </div>

        {loading && <div style={{ color: 'var(--muted-foreground)', fontSize: 14 }}>Betöltés…</div>}
        {error && <div className="alert alert-destructive"><span>⚠</span><div><p>{error}</p></div></div>}

        {!loading && !error && apps.length === 0 && (
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '48px 32px', textAlign: 'center', boxShadow: 'var(--shadow-md)' }}>
            <p style={{ color: 'var(--muted-foreground)', fontSize: 15 }}>Jelenleg nincs hozzárendelt pályázat.</p>
          </div>
        )}

        {!loading && !error && apps.length > 0 && (
          <>
            {/* Pending */}
            {pending.length > 0 && (
              <div style={{ marginBottom: 32 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#b07d00', display: 'inline-block' }} />
                  Pontozásra vár ({pending.length})
                </div>
                <AppTable apps={pending} onOpen={(id) => navigate(`/reviewer/applications/${id}`)} />
              </div>
            )}

            {/* Finalized */}
            {finalized.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted-foreground)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--secondary)', display: 'inline-block' }} />
                  Véglegesített ({finalized.length})
                </div>
                <AppTable apps={finalized} onOpen={(id) => navigate(`/reviewer/applications/${id}`)} />
              </div>
            )}
          </>
        )}
      </div>
    </ReviewerLayout>
  );
}

function AppTable({ apps, onOpen }: { apps: ReviewerApplicationSummary[]; onOpen: (id: string) => void }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}>
            {['Vállalkozás', 'Év / Típus', 'Státusz', 'Hozzárendelve', ''].map((h) => (
              <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted-foreground)' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {apps.map((app) => (
            <tr key={app.id} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '14px 16px' }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--primary)' }}>{app.companyName}</div>
                <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>{app.taxId}</div>
              </td>
              <td style={{ padding: '14px 16px', fontSize: 13, color: 'var(--muted-foreground)' }}>
                {app.year} · {app.type === 'new' ? 'Új' : 'Megújítás'}
              </td>
              <td style={{ padding: '14px 16px' }}>
                <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
                  {STATUS_LABELS[app.status] ?? app.status}
                </span>
              </td>
              <td style={{ padding: '14px 16px', fontSize: 12, color: 'var(--muted-foreground)' }}>
                {new Date(app.assignedAt).toLocaleDateString('hu-HU')}
              </td>
              <td style={{ padding: '14px 16px' }}>
                <button className="btn btn-primary btn-sm" onClick={() => onOpen(app.id)}>
                  {app.hasFinalized ? 'Megtekintés' : 'Pontozás →'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
